import { Modal, ModalWithTransition } from '@studiometa/ui';

/**
 * Facets component
 */
export default class Search extends ModalWithTransition {
  timeout = null;
  hiddenProductHandles = new Set();

  /**
   * Component Configuration
   */
  static config = {
    name: 'Search',
    refs: [...Modal.config.refs, 'form', 'input', 'results', 'loader'],
    options: {
      styles: {
        type: Object,
        /**
         * @returns {ModalStylesOption}
         */
        default: () => ({
          modal: {
            closed: {
              opacity: '0',
              pointerEvents: 'none',
            },
            active: 'transition duration-500 ease-out-expo',
          },
          overlay: {
            closed: 'opacity-0',
            active: 'transition duration-500 ease-out-expo',
          },
          container: {
            closed: 'transform scale-95 opacity-0',
            active: 'transition duration-500 ease-out-expo',
          },
        }),
      },
    },
  };

  /**
   * Initialize component - fetch hidden products list
   */
  mounted() {
    this.fetchHiddenProducts();
  }

  /**
   * Fetch products from 'hide' collection
   * @returns {Promise<void>}
   */
  async fetchHiddenProducts() {
    try {
      const response = await fetch('/collections/hide/products.json');
      if (response.ok) {
        const data = await response.json();
        this.hiddenProductHandles = new Set(data.products.map((product) => product.handle));
      }
    } catch (error) {
      console.warn('Could not fetch hidden products:', error);
    }
  }

  /**
   * Get input value
   * @returns {string} current value.
   */
  get searchTerm() {
    return this.$refs.input.value;
  }

  /**
   * Perform search when typing something in input
   * @returns {void}
   */
  onInputInput() {
    this.$refs.loader.style.display = 'block';

    clearTimeout(this.timeout);

    if (!this.searchTerm) {
      this.$refs.loader.style.display = 'none';
      return;
    }

    this.timeout = setTimeout(() => {
      fetch(
        `${window.routes.predictive_search_url}?q=${encodeURIComponent(this.searchTerm)}&section_id=predictive-search&resources[type]=product&resources[options][fields]=title,body,variants.title`,
      )
        .then((response) => {
          if (!response.ok) {
            const error = new Error(response.status);
            throw error;
          }
          return response.text();
        })
        .then((text) => {
          const results = new DOMParser()
            .parseFromString(text, 'text/html')
            .querySelector('#shopify-section-predictive-search').innerHTML;

          this.renderResults(results);
          this.$root.$update();
        })
        .catch((error) => {
          if (error?.code === 20) {
            // Code 20 means the call was aborted
            return;
          }
          throw error;
        });
    }, 800);
  }

  /**
   * Filter out hidden products from search results
   * @param {string} htmlString HTML string containing search results
   * @returns {string} Filtered HTML string
   */
  filterHiddenProducts(htmlString) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, 'text/html');

    // Find all product articles and check if they should be hidden
    const productArticles = doc.querySelectorAll('article');
    let removedCount = 0;

    productArticles.forEach((article) => {
      const productLink = article.querySelector('a[href*="/products/"]');
      if (productLink) {
        const productHandle = productLink.href.split('/products/')[1].split('?')[0].split('#')[0];

        // Remove product if it's in the hidden products list
        if (this.hiddenProductHandles.has(productHandle)) {
          article.remove();
          removedCount++;
        }
      }
    });

    // Update the product count if products were removed
    if (removedCount > 0) {
      const countElement = doc.querySelector('.type-subtitle');
      if (countElement) {
        const currentText = countElement.textContent;
        const currentCount = Number.parseInt(currentText.match(/\d+/)[0]);
        const newCount = currentCount - removedCount;
        countElement.textContent = currentText.replace(/\d+/, newCount);
      }
    }

    return doc.body.innerHTML;
  }

  /**
   * Set results to the page
   * @param  {HTML} results products results
   * @returns {void}
   */
  renderResults(results) {
    this.$refs.loader.style.display = 'none';
    const filteredResults = this.filterHiddenProducts(results);
    this.$refs.results.innerHTML = filteredResults;
  }

  /**
   * open
   */
  open() {
    document.scrollingElement.classList.add('is-search-open');
    super.open();
  }

  /**
   * close
   */
  close() {
    document.scrollingElement.classList.remove('is-search-open');
    super.close();
  }
}
